
import { Property, PropertyStatus, Translation, MaintenanceTask, TaskPriority, TaskStatus, InvestmentStrategy, RenovationProject, RenovationStatus, RenovationType, Contractor, ExpenseCategory, Entity, Tenant } from './types';

// Mock Entities
export const MOCK_ENTITIES: Entity[] = [
    { id: 'e1', name: 'Personal Holdings', type: 'Individual', taxId: '255999888' },
    { id: 'e2', name: 'PropVentures Lda', type: 'Company', taxId: '500123456' }
];

// Mock Tenants
export const MOCK_TENANTS: Tenant[] = [
    { 
        id: 't1', 
        name: 'João Silva', 
        email: 'joao.silva@example.com', 
        phone: '+351 912 345 678', 
        status: 'Active', 
        nif: '233444555',
        currentPropertyId: '1',
        rentAmount: 2200,
        leaseEnd: '2025-08-15',
        paymentDay: 5
    },
    { 
        id: 't2', 
        name: 'Maria Santos', 
        email: 'maria.s@example.com', 
        phone: '+351 910 000 111', 
        status: 'Active', 
        nif: '211222333',
        currentPropertyId: '2',
        rentAmount: 1600,
        leaseEnd: '2024-12-31',
        paymentDay: 1
    }
];

export const MOCK_PROPERTIES: Property[] = [
  {
    id: '1',
    entityId: 'e1', // Personal
    address: 'Rua da Prata 15, 2º Dto',
    city: 'Lisboa',
    zipCode: '1100-412',
    purchasePrice: 450000,
    currentValue: 520000,
    loanAmount: 300000,
    interestRate: 3.5,
    monthlyRent: 2200,
    expenses: 350,
    status: PropertyStatus.Occupied,
    strategy: InvestmentStrategy.BuyAndHold,
    imageUrl: 'https://picsum.photos/800/600?random=1',
    leaseEnd: '2025-08-15',
    tenantName: 'João Silva',
    tenantId: 't1',
    lat: 38.7106,
    lng: -9.1365,
    insuranceExpiry: '2024-11-30'
  },
  {
    id: '2',
    entityId: 'e2', // Company
    address: 'Avenida dos Aliados 42',
    city: 'Porto',
    zipCode: '4000-064',
    purchasePrice: 320000,
    currentValue: 365000,
    loanAmount: 250000,
    interestRate: 4.1,
    monthlyRent: 1600,
    expenses: 200,
    status: PropertyStatus.Occupied,
    strategy: InvestmentStrategy.ShortTerm,
    imageUrl: 'https://picsum.photos/800/600?random=2',
    leaseEnd: '2024-12-31',
    tenantName: 'Maria Santos',
    tenantId: 't2',
    lat: 41.1496,
    lng: -8.6110,
    insuranceExpiry: '2025-02-15'
  },
  {
    id: '3',
    entityId: 'e1',
    address: 'Estrada de Albufeira 101',
    city: 'Algarve',
    zipCode: '8200-001',
    purchasePrice: 280000,
    currentValue: 310000,
    loanAmount: 0,
    interestRate: 0,
    monthlyRent: 0,
    expenses: 150,
    status: PropertyStatus.Vacant,
    strategy: InvestmentStrategy.FixAndFlip,
    imageUrl: 'https://picsum.photos/800/600?random=3',
    lat: 37.0891,
    lng: -8.2459,
    insuranceExpiry: '2024-05-10'
  }
];

export const MOCK_TASKS: MaintenanceTask[] = [
  {
    id: 't1',
    propertyId: '1',
    title: 'Leaking Faucet',
    description: 'Kitchen faucet dripping constantly, tenant complained.',
    priority: TaskPriority.Low,
    status: TaskStatus.Pending,
    dateCreated: '2023-10-25',
    costEstimate: 50
  },
  {
    id: 't2',
    propertyId: '2',
    title: 'Gas Certification',
    description: 'Annual mandatory gas inspection renewal.',
    priority: TaskPriority.High,
    status: TaskStatus.Pending,
    dateCreated: '2023-10-20',
    costEstimate: 120
  },
  {
    id: 't3',
    propertyId: '3',
    title: 'Pool Cleaning',
    description: 'Pre-summer detailed pool maintenance.',
    priority: TaskPriority.Medium,
    status: TaskStatus.Completed,
    dateCreated: '2023-09-15',
    costEstimate: 200
  }
];

export const MOCK_CONTRACTORS: Contractor[] = [
  { id: 'c1', name: 'BuildFast Lda', specialty: 'General Contractor', hourlyRate: 45, rating: 4, phone: '+351 912 345 678' },
  { id: 'c2', name: 'Silva Electric', specialty: 'Electrician', hourlyRate: 35, rating: 5, phone: '+351 910 000 111' },
  { id: 'c3', name: 'AquaFix', specialty: 'Plumbing', hourlyRate: 40, rating: 3, phone: '+351 922 333 444' }
];

export const MOCK_RENOVATIONS: RenovationProject[] = [
  {
    id: 'r1',
    propertyId: '3', // Algarve Fix & Flip
    name: 'Summer Refresh',
    type: RenovationType.Cosmetic,
    status: RenovationStatus.InProgress,
    startDate: '2024-01-15',
    endDateEstimated: '2024-03-30',
    budget: 25000,
    description: 'Painting, new floors, and kitchen update for summer rental season.',
    tasks: [
      { id: 'rt1', title: 'Demolish Kitchen', status: TaskStatus.Completed, estimatedCost: 1000, actualCost: 1200, phase: 'Demo' },
      { id: 'rt2', title: 'Install Cabinets', status: TaskStatus.InProgress, estimatedCost: 5000, actualCost: 0, phase: 'Installation', contractorId: 'c1' }
    ],
    expenses: [
      { id: 'e1', date: '2024-01-20', description: 'Paint Supplies', amount: 450, category: ExpenseCategory.Materials },
      { id: 'e2', date: '2024-01-25', description: 'Demo Labor', amount: 1200, category: ExpenseCategory.Labor, contractorId: 'c1' }
    ]
  }
];

export const TRANSLATIONS: Translation = {
  dashboard: { en: 'Command Center', pt: 'Painel de Controlo' },
  properties: { en: 'Portfolio', pt: 'Portfólio' },
  tenants: { en: 'Tenants CRM', pt: 'Gestão Inquilinos' },
  deals: { en: 'Deal Flow', pt: 'Oportunidades' },
  tools: { en: 'Calculators', pt: 'Calculadoras' },
  maintenance: { en: 'Operations', pt: 'Operações' },
  analytics: { en: 'Analytics', pt: 'Analítica' },
  ai_assistant: { en: 'AI Advisor', pt: 'Consultor IA' },
  renovations: { en: 'Renovations', pt: 'Obras' },
  scenarios: { en: 'Strategy Sim', pt: 'Simulador Estrat.' },
  glossary: { en: 'Glossary', pt: 'Glossário' },
  total_value: { en: 'Gross Asset Value', pt: 'Valor Bruto de Ativos' },
  monthly_income: { en: 'Est. Monthly Revenue', pt: 'Receita Mensal Est.' },
  occupancy_rate: { en: 'Occupancy', pt: 'Ocupação' },
  avg_yield: { en: 'Portfolio Yield', pt: 'Yield do Portfólio' },
  recent_activity: { en: 'Live Activity', pt: 'Atividade em Tempo Real' },
  welcome: { en: 'Welcome back, Investor', pt: 'Bem-vindo, Investidor' },
  calculate_taxes: { en: 'Calculate Purchase Taxes', pt: 'Calcular Impostos de Compra' },
  property_value: { en: 'Asset Value', pt: 'Valor do Ativo' },
  location: { en: 'Location', pt: 'Localização' },
  calculate: { en: 'Calculate', pt: 'Calcular' },
  ask_ai: { en: 'Ask about Portuguese Real Estate', pt: 'Pergunte sobre Imobiliário em Portugal' },
  pending_tasks: { en: 'Active Alerts', pt: 'Alertas Ativos' },
  mortgage_calc: { en: 'Leverage Calc', pt: 'Calc. Alavancagem' },
  deal_analyzer: { en: 'Deal Screener', pt: 'Análise de Negócio' },
  tax_simulator: { en: 'Tax Engine', pt: 'Motor Fiscal' },
  monthly_payment: { en: 'Monthly Payment', pt: 'Mensalidade' },
  total_interest: { en: 'Total Interest', pt: 'Juros Totais' },
  down_payment: { en: 'Capital Entry', pt: 'Entrada de Capital' },
  interest_rate: { en: 'Cost of Debt (%)', pt: 'Custo da Dívida (%)' },
  loan_term: { en: 'Loan Term (Years)', pt: 'Prazo (Anos)' },
  cap_rate: { en: 'Cap Rate', pt: 'Cap Rate' },
  cash_on_cash: { en: 'Cash on Cash', pt: 'Retorno s/ Capital' },
  poi: { en: 'Profit on Investment', pt: 'Lucro do Investimento' },
  task_status: { en: 'Status', pt: 'Estado' },
  task_priority: { en: 'Priority', pt: 'Prioridade' },
};
